/**
 * 实用能力展示入口（帮助 / 反馈 / 客服 / AI）
 * 基于 DisplaySurface，统一 link | modal 等模式与 page_url 自动填充
 */

import {
  DisplayMode,
  openSurface,
  closeSurface,
  buildUrl,
  getPageContext,
  mergeSurfaceParams,
} from './displaySurface.js';

/**
 * @param {Object} options
 * @param {string} options.url
 * @param {string} [options.mode='modal']
 * @param {Record<string, any>} [options.params]
 * @param {boolean} [options.autoPageContext=true]
 */
export function openUtilitySurface(options = {}) {
  const {
    url,
    mode = DisplayMode.MODAL,
    params = {},
    autoPageContext = true,
    title,
    ...rest
  } = options;

  return openSurface({
    url,
    mode,
    params,
    autoPageContext,
    title,
    ...rest,
  });
}

export function openFeedbackSurface(options = {}) {
  const {
    url = '/feedback',
    mode = DisplayMode.MODAL,
    title = 'Feedback',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openHelpSurface(options = {}) {
  const {
    url = '/help',
    mode = DisplayMode.MODAL,
    title = 'Help',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openHelpdeskSurface(options = {}) {
  const {
    url = '/helpdesk',
    mode = DisplayMode.MODAL,
    title = 'Support',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openAISurface(options = {}) {
  const {
    url = '/ai',
    mode = DisplayMode.MODAL,
    title = 'AI Assistant',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openReportSurface(options = {}) {
  const {
    url = '/report',
    mode = DisplayMode.MODAL,
    title = 'Report',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openSubscriptionSurface(options = {}) {
  const {
    url = '/subscription',
    mode = DisplayMode.MODAL,
    title = 'Subscription',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openCheckinSurface(options = {}) {
  const {
    url = '/checkin',
    mode = DisplayMode.MODAL,
    title = 'Check-in',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openInviteSurface(options = {}) {
  const {
    url = '/invite',
    mode = DisplayMode.MODAL,
    title = 'Invite',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openShareSurface(options = {}) {
  const {
    url = '/share',
    mode = DisplayMode.POPUP,
    title = 'Share',
    params = {},
    popupOptions = { width: 600, height: 480 },
    ...rest
  } = options;

  return openUtilitySurface({
    url,
    mode,
    title,
    params,
    popupOptions,
    ...rest,
  });
}

export function openTipSurface(options = {}) {
  const {
    url = '/tip',
    mode = DisplayMode.MODAL,
    title = 'Tip',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openChatSurface(options = {}) {
  const {
    url = '/chat',
    mode = DisplayMode.MODAL,
    title = 'Chat',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openAIAgentSurface(options = {}) {
  const {
    url = '/ai-agent',
    mode = DisplayMode.MODAL,
    title = 'AI Agent',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

export function openDonateSurface(options = {}) {
  const {
    url = '/donate',
    mode = DisplayMode.MODAL,
    title = 'Donate',
    params = {},
    ...rest
  } = options;

  return openUtilitySurface({ url, mode, title, params, ...rest });
}

/**
 * 为 API 提交载荷自动合并页面上下文
 * @param {Record<string, any>} [payload={}]
 * @param {Object} [options={}]
 * @returns {Record<string, any>}
 */
export function withPageContext(payload = {}, options = {}) {
  const ctx = getPageContext(options);
  const {
    extra_data: extraData,
    ...restPayload
  } = payload || {};

  return {
    platform: ctx.platform,
    app_id: ctx.app_id,
    app_name: ctx.app_name,
    domain: ctx.domain,
    page_url: ctx.page_url,
    ...restPayload,
    extra_data: {
      screenResolution: ctx.screen,
      language: ctx.language,
      path: ctx.path,
      title: ctx.title,
      referrer: ctx.referrer,
      ...(extraData || {}),
    },
  };
}

export {
  DisplayMode,
  openSurface,
  closeSurface,
  buildUrl,
  getPageContext,
  mergeSurfaceParams,
};

export default {
  DisplayMode,
  openUtilitySurface,
  openFeedbackSurface,
  openHelpSurface,
  openHelpdeskSurface,
  openAISurface,
  openReportSurface,
  openSubscriptionSurface,
  openCheckinSurface,
  openInviteSurface,
  openShareSurface,
  openTipSurface,
  openChatSurface,
  openAIAgentSurface,
  openDonateSurface,
  withPageContext,
};
